import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { searchBusinesses } from 'api/admin/businesses/businesses';

type RawBusiness = { id: number; name: string; [key: string]: unknown };
type BusinessOption = { value: number; display: string };

export function useBusinessSearch() {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: rawBusinesses = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['businessSearch', { searchTerm }],
    queryFn: async () => {
      const { data } = await searchBusinesses(searchTerm);
      return (data.businesses ?? []) as RawBusiness[];
    },
    keepPreviousData: true,
    retry: 1,
  });

  const businessOptions: BusinessOption[] = useMemo(
    () => rawBusinesses.map((b) => ({ value: b.id, display: `${b.name} [ID: ${b.id}]` })),
    [rawBusinesses],
  );

  const onInputChange = useMemo(
    () =>
      debounce((_: unknown, value: string, reason?: string) => {
        // MUI fires a 'reset' input change (with the selected option's label) when an
        // option is chosen or the field loses focus — searching for that label would
        // clobber the current search results, so only react to genuine typing.
        if (reason === 'reset') return;
        setSearchTerm(value);
      }, 300),
    [],
  );

  useEffect(() => () => onInputChange.cancel(), [onInputChange]);

  return {
    businessOptions,
    rawBusinesses,
    isLoading,
    error: (queryError as any)?.response?.data?.error ?? (queryError as any)?.message ?? null,
    onInputChange,
    filterOptions: (x: BusinessOption[]): BusinessOption[] => x,
  };
}
